import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { CollectionGameHelper } from '@sorare/core/src/components/collections/CollectionGameHelper';
import { Dialog } from '@sorare/core/src/components/dialog';

import { AllStepsProgressBar } from '#src/components/collections/AllStepsProgressBar/index.tsx';

import type { DetailsDialog_cardCollection } from './__generated__/index.graphql.ts';

const Root = styled(Vertical).attrs({ gap: 3 })`
  padding: 0 var(--triple-unit) var(--triple-unit);
`;
const Separator = styled.div`
  height: 1px;
  background: var(--c-nd-150);
`;

type Props = {
  onClose: () => void;
  open: boolean;
  cardCollection: Nullable<DetailsDialog_cardCollection>;
  score?: number;
};
export const DetailsDialog = ({
  onClose,
  open,
  cardCollection,
  score,
}: Props) => {
  return (
    <Dialog
      open={open}
      maxWidth="xs"
      fullWidth
      onClose={onClose}
      title={
        <LabelS color="var(--c-nd-600)" bold>
          <FormattedMessage
            id="collections.DetailsDialog.title"
            defaultMessage="The Collection Game"
          />
        </LabelS>
      }
    >
      <Root>
        <CollectionGameHelper cardCollection={cardCollection} />
        {!!score && (
          <>
            <Separator />
            <Vertical gap={1.5}>
              <LabelS bold color="var(--c-nd-600)">
                <FormattedMessage
                  id="collections.DetailsDialog.progress"
                  defaultMessage="Current Progress"
                />
              </LabelS>
              {!!cardCollection && (
                <AllStepsProgressBar
                  cardCollection={cardCollection}
                  score={score}
                />
              )}
            </Vertical>
          </>
        )}
      </Root>
    </Dialog>
  );
};

DetailsDialog.fragments = {
  cardCollection: gql`
    fragment DetailsDialog_cardCollection on CardCollection {
      slug
      ...CollectionGameHelper_cardCollection
      ...AllStepsProgressBar_cardCollection
    }
    ${CollectionGameHelper.fragments.cardCollection}
    ${AllStepsProgressBar.fragments.cardCollection}
  ` as TypedDocumentNode<DetailsDialog_cardCollection>,
};
